import React, { useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import get from "lodash/get";
import { Box, Table, TablePagination, SxProps, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { WithLoadingSpinner } from "@components";
import { useTableSortWithData } from "@hooks";
import { GroupByConfig, TableConfig } from "../types.ts";
import { useGroupedData } from "./useGroupedData.ts";
import { AutoTableHead } from "./AutoTableHead.tsx";
import { AutoTableBody } from "./AutoTableBody.tsx";

export type InfiniteScrollConfig = {
  hasMore: boolean;
  next: () => void;
  loader?: React.ReactNode;
  scrollableTarget?: string;
};

type RowBase = { children?: any[] };

type Props<DataType> = {
  settings: TableConfig<DataType>;
  data: DataType[];
  isPending?: boolean;
  isFetching?: boolean;
  className?: string;
  additionalProps?: Record<string, unknown>;
  showNoData?: boolean;
  paginate?: boolean;
  perPageOptions?: number[];
  noDataFirstLine?: string;
  noDataSecondLine?: string;
  sx?: SxProps<Theme>;
  stickyHeader?: boolean;
  groupBy?: GroupByConfig<DataType>;
  scrollToGroup?: string | number;
  infiniteScroll?: InfiniteScrollConfig;
};

function InternalAutoTable<Data extends RowBase>(
  inputProps: Props<Data> & { ref?: React.Ref<HTMLDivElement> }
) {
  const { t } = useTranslation();

  const {
    ref,
    settings,
    data,
    isPending = false,
    isFetching = false,
    className,
    additionalProps,
    showNoData,
    paginate,
    perPageOptions = [20, 50, 100],
    sx,
    noDataFirstLine,
    noDataSecondLine,
    stickyHeader,
    groupBy,
    scrollToGroup,
    infiniteScroll,
  } = inputProps;

  const isSortControlled = Boolean(settings.onSort);

  const containerRef = useRef<HTMLDivElement>(null);
  const scrolledToRef = useRef<string | number | undefined>(undefined);

  const [pagination, setPagination] = useState<{
    page: number;
    perPage: number;
  }>({
    page: 0,
    perPage: perPageOptions[0] ?? 20,
  });

  const getSortValue = (row: Data, sortField: string) => {
    const col = settings.columns.find((c) => (c.sortKey ?? c.id) === sortField);
    return col?.sortValue ? col.sortValue(row) : get(row, sortField);
  };

  const internalSort = useTableSortWithData({
    data,
    defaultSort: settings.sorting && {
      field: settings.sorting.field,
      direction: settings.sorting.direction,
    },
    enabled: !groupBy && !isSortControlled,
    getSortValue,
    saveKey: settings.sorting?.saveKey,
  });

  const sortedData = isSortControlled ? data : internalSort.data;
  const sortField = isSortControlled
    ? settings.sorting?.field
    : internalSort.sortField;
  const sortDirection = isSortControlled
    ? settings.sorting?.direction
    : internalSort.sortDirection;
  const sort = isSortControlled ? settings.onSort! : internalSort.sort;

  const hasExpandableRows = useMemo(
    () => data.some((row) => settings.isRowExpandable?.(row)),
    [data, settings]
  );

  const pagedData = useMemo(() => {
    if (!paginate) return sortedData;
    return sortedData.slice(
      pagination.page * pagination.perPage,
      (pagination.page + 1) * pagination.perPage
    );
  }, [sortedData, pagination.page, pagination.perPage, paginate]);

  const groupedData = useGroupedData({
    groupBy,
    data: pagedData,
    sortField,
    sortDirection,
  });

  useEffect(() => {
    if (scrollToGroup === undefined || !containerRef.current) return;
    if (scrolledToRef.current === scrollToGroup) return;
    const el = containerRef.current.querySelector(
      `[data-group="${scrollToGroup}"]`
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      scrolledToRef.current = scrollToGroup;
    }
  }, [scrollToGroup, groupedData]);

  const tableElement = (
    <Table className={className} stickyHeader={stickyHeader}>
      <AutoTableHead
        settings={settings}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={sort}
        hasExpandableRows={hasExpandableRows}
      />
      <AutoTableBody
        settings={settings}
        isPending={isPending}
        showNoData={showNoData}
        noDataFirstLine={noDataFirstLine ?? t("form.errors.noData")}
        noDataSecondLine={noDataSecondLine}
        groupedData={groupedData}
        pagedData={pagedData}
        groupBy={groupBy}
        hasExpandableRows={hasExpandableRows}
        additionalProps={additionalProps}
      />
    </Table>
  );

  return (
    <WithLoadingSpinner isLoading={isFetching && !isPending} minHeight={300}>
      <Box
        sx={{
          overflow: "auto",
          maxHeight: stickyHeader ? "calc(100dvh - 26px)" : undefined,
          position: "relative",
          ...sx,
        }}
        ref={(node: HTMLDivElement | null) => {
          containerRef.current = node;
          if (typeof ref === "function") ref(node);
        }}
      >
        {infiniteScroll ? (
          <InfiniteScroll
            dataLength={pagedData.length}
            next={infiniteScroll.next}
            hasMore={infiniteScroll.hasMore}
            loader={infiniteScroll.loader ?? null}
            scrollableTarget={infiniteScroll.scrollableTarget}
          >
            {tableElement}
          </InfiniteScroll>
        ) : (
          tableElement
        )}
        {paginate && (
          <TablePagination
            component="div"
            page={pagination.page}
            rowsPerPageOptions={perPageOptions}
            count={sortedData.length}
            rowsPerPage={pagination.perPage}
            onPageChange={(_, newPage) =>
              setPagination({ ...pagination, page: newPage })
            }
            onRowsPerPageChange={(event) =>
              setPagination({
                page: 0,
                perPage: Number(event.target.value),
              })
            }
          />
        )}
      </Box>
    </WithLoadingSpinner>
  );
}

export const AutoTable = InternalAutoTable as <T>(
  p: Props<T> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof InternalAutoTable>;
